import React, { useRef } from 'react'
import { Box, Typography, TextField, Paper, Button, Checkbox, FormControlLabel } from '@mui/material'
import SignatureCanvas from 'react-signature-canvas'
import { format } from 'date-fns'

interface AdministratorCertificationProps {
  agencyName: string
  administratorName: string
  administratorTitle: string
  hasSignature: boolean
  onSignatureChange: (dataUrl: string | null) => void
  autopayAuthorized: boolean
  onAutopayChange: (checked: boolean) => void
  eSignConsent: boolean
  onESignChange: (checked: boolean) => void
}

const AdministratorCertification: React.FC<AdministratorCertificationProps> = ({
  agencyName,
  administratorName,
  administratorTitle,
  hasSignature,
  onSignatureChange,
  autopayAuthorized,
  onAutopayChange,
  eSignConsent,
  onESignChange,
}) => {
  const signaturePadRef = useRef<SignatureCanvas>(null)

  const handleSignatureEnd = () => {
    if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
      onSignatureChange(signaturePadRef.current.toDataURL('image/png'))
    } else {
      onSignatureChange(null)
    }
  }

  const handleClearSignature = () => {
    signaturePadRef.current?.clear()
    onSignatureChange(null)
  }

  return (
    <Box
      sx={{
        border: '2px solid #C3B8E4',
        borderRadius: 3,
        bgcolor: 'white',
        p: { xs: 2.5, sm: 3 },
        mt: 2,
      }}
    >
      <Typography sx={{ color: '#7A6FA1', fontWeight: 800, fontSize: '17px', mb: 1.5 }}>
        Administrator certification
      </Typography>

      <Box component="ul" sx={{ pl: 2.5, mb: 2.5, fontSize: '14.5px', color: '#4B5563' }}>
        <li>I am the Primary Agency Administrator and have authority to bind this agency.</li>
        <li>I have read and understand the Agency Platform Subscription Agreement and each point above.</li>
        <li>I authorize PillarThera to charge subscription fees and deduct the Per-Claim System Fee as described.</li>
        <li>I understand the subscription renews automatically each month until cancelled.</li>
        <li>The information provided during registration is accurate to the best of my knowledge.</li>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mb: 2 }}>
        <TextField
          label="Agency legal business name"
          value={agencyName}
          disabled
          helperText="From your registration. Contact support to correct it."
          fullWidth
          size="small"
        />
        <TextField
          label="Date"
          value={format(new Date(), 'MM/dd/yyyy')}
          disabled
          fullWidth
          size="small"
        />
        <TextField
          label="Administrator full name"
          required
          value={administratorName}
          InputProps={{ readOnly: true }}
          helperText="From your registration. Contact support to correct it."
          fullWidth
          size="small"
        />
        <TextField
          label="Title"
          value={administratorTitle}
          disabled
          helperText="Set for the Primary Agency Administrator."
          fullWidth
          size="small"
        />
      </Box>

      <Box sx={{ mb: 1 }}>
        <Typography sx={{ fontSize: '13.5px', fontWeight: 600, color: '#4B5563', mb: 0.75, display: 'flex', alignItems: 'center', gap: 1 }}>
          Signature <Box component="span" sx={{ color: '#DC3545' }}>*</Box>
          {hasSignature && (
            <Box component="span" sx={{ color: '#1E7A4E', fontWeight: 700, fontSize: '12.5px' }}>
              &#10003; Captured
            </Box>
          )}
        </Typography>
        <Paper
          variant="outlined"
          sx={{
            borderRadius: 2,
            p: 1,
            bgcolor: 'white',
            display: 'flex',
            justifyContent: 'center',
            overflow: 'auto',
            '& canvas': {
              backgroundColor: 'white',
              borderRadius: '4px',
              cursor: 'crosshair',
              maxWidth: '100%',
              height: 'auto',
            },
          }}
        >
          <SignatureCanvas
            ref={signaturePadRef}
            canvasProps={{
              width: 1000,
              height: 150,
              className: 'signature-canvas',
              style: { maxWidth: '100%', height: 'auto' },
            }}
            onEnd={handleSignatureEnd}
          />
        </Paper>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1, flexWrap: 'wrap', gap: 1 }}>
          <Button size="small" variant="outlined" onClick={handleClearSignature} sx={{ textTransform: 'none' }}>
            Clear signature
          </Button>
          <Typography sx={{ fontSize: '12.5px', color: '#7C8794' }}>
            Draw with your mouse, finger, or stylus.
          </Typography>
        </Box>
      </Box>

      <FormControlLabel
        sx={{ mt: 1.5, alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={autopayAuthorized}
            onChange={e => onAutopayChange(e.target.checked)}
            sx={{ mt: -0.5 }}
          />
        }
        label={
          <Typography sx={{ fontSize: '13.5px', color: '#4B5563' }}>
            I authorize recurring monthly charges to the payment method on file and automatic deduction of the
            Per-Claim System Fee from settlements through our Stripe Connect account.
          </Typography>
        }
      />

      <FormControlLabel
        sx={{ mt: 0.5, alignItems: 'flex-start' }}
        control={
          <Checkbox
            checked={eSignConsent}
            onChange={e => onESignChange(e.target.checked)}
            sx={{ mt: -0.5 }}
          />
        }
        label={
          <Typography sx={{ fontSize: '13.5px', color: '#4B5563' }}>
            I agree to sign electronically. My electronic signature, initials, and the associated timestamps and
            audit records have the same legal effect as a handwritten signature under the E-SIGN Act and UETA.
          </Typography>
        }
      />
    </Box>
  )
}

export default AdministratorCertification
